/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';

import { token } from '@atlaskit/tokens';

import { Box, xcss } from '@atlaskit/primitives';
import { LozengeAndAttributeValueModal } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/main.tsx';
import { MULTI_VALUE_ATTRIBUTE_THRESHOLD } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/index.tsx';
import { getBBRepoValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/bitbucket-repo/index.tsx';
import { getDefaultAttributeArrayValueFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { getGroupValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/group/index.tsx';
import { getOpsgenieTeamValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/opsgenie-team/index.tsx';
import { getProjectValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/project/index.tsx';
import { getObjectReferenceOptionsFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/referenced-object/index.tsx';
import { getUserValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/user/index.tsx';
import {
	DefaultAttributeSubtypeMap,
	AttributeTypeMap,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-types.tsx';
import type {
	CmdbReferencedObject,
	CmdbObjectAttributeValueDefault,
	CmdbObjectAttributeValueUser,
	CmdbObjectAttributeValueGroup,
	CmdbObjectAttributeValueStatus,
	CmdbObjectAttributeValueObject,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import type {
	CmdbObjectAttribute,
	CmdbObjectAttributeDefault,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { BitbucketRepoAttribute } from './bitbucket-repo-attribute/index.tsx';
import { BooleanAttribute } from './boolean-attribute/index.tsx';
import { ObjectAttribute } from './button-attribute-with-avatar/index.tsx';
import { DateAttribute, DateTimeAttribute } from './date-and-time-attribute/index.tsx';
import { GroupAttribute } from './group-attribute/index.tsx';
import { OpsgenieTeamAttribute } from './opsgenie-team-attribute/index.tsx';
import { ProjectAttribute } from './project-attribute/index.tsx';
import { StatusAttribute } from './status-attribute/index.tsx';
import { TextAttribute, RichTextAttribute } from './text-attribute/index.tsx';
import { UnknownAttribute } from './unknown-attribute/index.tsx';
import { UrlAttribute, EmailAttribute } from './url-attribute/index.tsx';
import { UserAttribute } from './user-attribute/index.tsx';

export type Props = {
	attributes: CmdbObjectAttribute[];
	objectId: CmdbObjectId;
	onObjectAttributeClick: (arg1: CmdbReferencedObject) => void;
};

export const AttributesList = ({ attributes, objectId, onObjectAttributeClick }: Props) => {
	const renderDefaultAttribute = (attribute: CmdbObjectAttributeDefault) => {
		switch (attribute.objectTypeAttribute.defaultType.id.toString()) {
			case DefaultAttributeSubtypeMap.Boolean:
				return <BooleanAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.URL:
				return <UrlAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Email:
				return <EmailAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Textarea:
				return <RichTextAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Date:
				return <DateAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.DateTime:
				return <DateTimeAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Select:
				return attribute.objectAttributeValues.length > MULTI_VALUE_ATTRIBUTE_THRESHOLD ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'select',
							attributeValues: getDefaultAttributeArrayValueFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.select-lozenge"
					/>
				) : (
					<TextAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.select-attribute"
					/>
				);
			case DefaultAttributeSubtypeMap.Text:
			case DefaultAttributeSubtypeMap.Integer:
			case DefaultAttributeSubtypeMap.Double:
			case DefaultAttributeSubtypeMap.IPAddress:
			case DefaultAttributeSubtypeMap.Time:
			default:
				return <TextAttribute attribute={attribute} />;
		}
	};

	const renderAttribute = (attribute: CmdbObjectAttribute) => {
		const showScalableLozengeButton =
			attribute.objectAttributeValues.length > MULTI_VALUE_ATTRIBUTE_THRESHOLD;
		switch (attribute.type) {
			case AttributeTypeMap.Default:
				return renderDefaultAttribute(attribute);
			case AttributeTypeMap.Group:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'group',
							attributeValues: getGroupValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.group-lozenge"
					/>
				) : (
					<GroupAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.group-attribute"
					/>
				);
			case AttributeTypeMap.Status:
				return <StatusAttribute attribute={attribute} />;
			case AttributeTypeMap.Object:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'object',
							attributeValues: getObjectReferenceOptionsFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.object-lozenge"
					/>
				) : (
					<ObjectAttribute
						attribute={attribute}
						onClick={onObjectAttributeClick}
						data-testid="servicedesk-insight-attribute-list.ui.object-attribute"
					/>
				);
			case AttributeTypeMap.User:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'user',
							attributeValues: getUserValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.user-lozenge"
					/>
				) : (
					<UserAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.user-attribute"
					/>
				);
			case AttributeTypeMap.Project:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'project',
							attributeValues: getProjectValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.project-lozenge"
					/>
				) : (
					<ProjectAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.project-attribute"
					/>
				);
			case AttributeTypeMap.BitbucketRepo:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'bitbucket',
							attributeValues: getBBRepoValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.bitbucket-lozenge"
					/>
				) : (
					<BitbucketRepoAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.bitbucket-attribute"
					/>
				);
			case AttributeTypeMap.OpsgenieTeam:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'opsgenieTeam',
							attributeValues: getOpsgenieTeamValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.opsgenie-team-lozenge"
					/>
				) : (
					<OpsgenieTeamAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.opsgenie-team-attribute"
					/>
				);
			default:
				return <UnknownAttribute attribute={attribute} objectId={objectId} />;
		}
	};

	const isTextarea = (attribute: CmdbObjectAttribute) =>
		attribute.type === AttributeTypeMap.Default &&
		attribute.objectTypeAttribute.defaultType.id === DefaultAttributeSubtypeMap.Textarea;

	const objectAttributeValuesAreNotEmpty = (
		values:
			| CmdbObjectAttributeValueDefault[]
			| CmdbObjectAttributeValueUser[]
			| CmdbObjectAttributeValueGroup[]
			| CmdbObjectAttributeValueStatus[]
			| CmdbObjectAttributeValueObject[],
	): boolean => values.every((value) => Object.keys(value).length > 0);

	return (
		<Box xcss={ListWrapperStyles}>
			<Box as="dl" xcss={ListStyles}>
				{attributes.map(
					(attribute) =>
						objectAttributeValuesAreNotEmpty(attribute.objectAttributeValues) && (
							<Box
								key={attribute.objectTypeAttributeId}
								testId="servicedesk-insight-attribute-list.ui.attribute-wrapper"
								xcss={[
									AttributeWrapperStyles,
									isTextarea(attribute) && AttributeWrapperColumnStyles,
								]}
							>
								<AttributeLabel isColumn={isTextarea(attribute)}>
									{attribute.objectTypeAttribute.name}
								</AttributeLabel>
								<Box
									as="dd"
									xcss={[AttributeValueStyles, isTextarea(attribute) && AttributeValueColumnStyles]}
								>
									{renderAttribute(attribute)}
								</Box>
							</Box>
						),
				)}
			</Box>
		</Box>
	);
};

const AttributeLabel = ({ isColumn, children }: { isColumn: boolean; children: ReactNode }) => (
	<Box as="dt" xcss={[AttributeLabelWrapperStyles, isColumn && AttributeLabelWrapperColumnStyles]}>
		<Heading size="xsmall" as="span">
			<Box as="span" xcss={attributeLabelStyles}>
				{children}
			</Box>
		</Heading>
	</Box>
);

const ListStyles = xcss({
	paddingLeft: 'space.0', // dl has 40px default padding
});

const ListWrapperStyles = xcss({
	/* Offset the outside padding from children */
	// Half vertical space between each attribute, half horizontal space between label & value
	marginTop: 'space.negative.050',
	marginRight: 'space.negative.150',
	marginBottom: 'space.negative.050',
	marginLeft: 'space.negative.150',
});

const attributeLabelStyles = xcss({ color: 'color.text.subtle' });

const AttributeLabelWrapperStyles = xcss({
	marginBlock: 'space.050',
	marginInline: 'space.150',
	minHeight: token('space.300'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.300'),
	flexBasis: '112px',
	flexGrow: 1,
});

const AttributeLabelWrapperColumnStyles = xcss({
	marginTop: 'space.050',
	marginRight: 'space.150',
	marginBottom: 'space.100',
	marginLeft: 'space.150',
	flexBasis: 'auto',
});

const AttributeValueStyles = xcss({
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
	wordBreak: 'break-all',
	color: 'color.text',
	// Half vertical space between each attribute, half horizontal space between label & value
	marginTop: 'space.050',
	marginRight: 'space.150',
	marginBottom: 'space.050',
	marginLeft: 'space.150',
	minHeight: token('space.300'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.300'),
	flexBasis: '224px',
	flexGrow: 1,
});

const AttributeValueColumnStyles = xcss({
	flexBasis: 'auto',
});

const AttributeWrapperStyles = xcss({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'row',
});

const AttributeWrapperColumnStyles = xcss({
	flexDirection: 'column',
});
